import React from 'react';
import { motion } from 'framer-motion';
import ServicesGrid from '../components/ServicesGrid';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import JsonLd from '../components/JsonLd';
import { servicesData } from '../mockData';

const Services = () => {
  useSEO({
    title: 'Web Design Services in Singapore | Hei Web Creations',
    description:
      'Explore our services: web design, WordPress, WooCommerce, portals, AI-assisted creation, revamps and ongoing maintenance for Singapore SMEs & startups.',
  });

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
      { '@type': 'ListItem', position: 2, name: 'Services', item: `${origin}/services` },
    ],
  };
  const serviceListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: servicesData.map((s, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${origin}/services/${s.slug}`,
      name: s.title,
    })),
  };

  return (
    <div className="pt-32 bg-white" data-testid="services-page">
      <JsonLd id="breadcrumb-services" data={breadcrumbSchema} />
      <JsonLd id="services-itemlist" data={serviceListSchema} />
      {/* Hero Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#1A1A1A] mb-6 tracking-tight">
              Our Services
            </h1>
            <p className="text-xl md:text-2xl text-gray-500 leading-relaxed">
              Comprehensive web solutions tailored to your business needs. From design to development, we've got you covered.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid - Show All */}
      <ServicesGrid showAll={true} />

      {/* Process Section */}
      <section className="py-32 bg-[#FAFAFA]">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1A1A1A] mb-6 tracking-tight">
              Our Process
            </h2>
            <p className="text-lg md:text-xl text-gray-500 max-w-3xl mx-auto">
              A streamlined approach that delivers results
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Discovery',
                description: 'We understand your goals, audience, and requirements',
              },
              {
                step: '02',
                title: 'Strategy',
                description: 'We craft a tailored solution and project roadmap',
              },
              {
                step: '03',
                title: 'Design & Develop',
                description: 'We bring your vision to life with premium execution',
              },
              {
                step: '04',
                title: 'Launch & Support',
                description: 'We ensure smooth deployment and ongoing success',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-6xl font-bold text-[#C8102E]/20 mb-4">
                  {item.step}
                </div>
                <h3 className="text-2xl font-bold text-[#1A1A1A] mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-500">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto bg-[#1A1A1A] rounded-[2.5rem] px-8 py-20 md:px-16"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              Ready to get started?
            </h2>
            <p className="text-lg md:text-xl text-gray-400 mb-12 leading-relaxed max-w-2xl mx-auto">
              Let's discuss your project and create something exceptional together.
            </p>
            <Link to="/contact">
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="inline-block">
                <Button
                  className="bg-[#C8102E] hover:bg-white hover:text-[#1A1A1A] text-white px-10 py-6 text-base rounded-full transition-all duration-300"
                  data-testid="services-cta-button"
                >
                  Contact Us Today
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </motion.div>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
